/*
  Client-side vocabulary for supplier payment intents (a Ponto payment created from an incoming invoice).
  The server computes the state (services/paymentIntentRules.js); this file only knows how to show it.

    awaiting_signature  created in Ponto, nobody signed it yet (can still be cancelled)
    processing          signed — the bank is executing it, the settling transaction has not arrived yet
    settled             the debit was matched and booked: the invoice is paid
    failed              rejected / cancelled / error: the invoice is simply still open
*/
export const INTENT_STATE_META = {
  awaiting_signature: { label: 'Awaiting signature', short: 'To sign', tone: 'attention', hint: 'Sent to Ponto — it still has to be signed before the bank executes it.' },
  processing: { label: 'Sent to bank', short: 'At bank', tone: 'info', hint: 'Signed — the bank is executing the transfer. The invoice is marked paid as soon as the debit is matched.' },
  settled: { label: 'Paid via bank', short: 'Paid', tone: 'success', hint: 'The bank transaction was matched and booked on the invoice.' },
  failed: { label: 'Payment failed', short: 'Failed', tone: 'critical', hint: 'Rejected or cancelled — the invoice is still open.' }
};

const DEAD_STATUSES = ['rejected', 'cancelled', 'failed', 'error', 'not-found'];
const SIGNATURE_STATUSES = ['created', 'unsigned', 'waiting-for-signature'];

// mirror of the server rule, for rows that only carry Ponto's raw status
export function intentStateForStatus(status) {
  const value = String(status || '').toLowerCase();
  if (value === 'settled') return 'settled';
  if (DEAD_STATUSES.includes(value)) return 'failed';
  if (SIGNATURE_STATUSES.includes(value)) return 'awaiting_signature';
  return 'processing';
}

export function intentMeta(stateOrStatus) {
  return INTENT_STATE_META[stateOrStatus] || INTENT_STATE_META[intentStateForStatus(stateOrStatus)];
}

export function isActiveIntent(intent) {
  const state = intent?.state || intentStateForStatus(intent?.Status);
  return state === 'awaiting_signature' || state === 'processing';
}

export function formatIban(value) {
  const iban = String(value || '')
    .replace(/[\s.\-]/g, '')
    .toUpperCase();
  return iban.match(/.{1,4}/g)?.join(' ') || iban;
}

export function normalizeIban(value) {
  return String(value || '')
    .replace(/[\s.\-]/g, '')
    .toUpperCase();
}

// same checksum as the server (ISO 13616 mod 97), so the form can flag typos before sending
export function isValidIban(value) {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const digits = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, (letter) => String(letter.charCodeAt(0) - 55));
  let remainder = 0;
  for (let i = 0; i < digits.length; i += 7) remainder = Number(String(remainder) + digits.slice(i, i + 7)) % 97;
  return remainder === 1;
}

export const PONTO_DASHBOARD_URL = 'https://dashboard.myponto.com';

// where a pending payment gets signed: the payment's own link when Ponto returned one, else the Ponto dashboard (Payments → To sign)
export function signingTarget(intent) {
  return intent?.SigningRedirectLink ? { url: intent.SigningRedirectLink, label: 'Sign' } : { url: PONTO_DASHBOARD_URL, label: 'Sign in Ponto' };
}

export const IBAN_SOURCE_LABELS = {
  peppol: 'Peppol',
  invoice: 'This invoice',
  supplier_invoices: 'Earlier invoices',
  bank_history: 'Bank history',
  pdf: 'Invoice PDF'
};

// small fetch wrapper that keeps the HTTP status (getRequest swallows it)
export async function intentApi(path, { method = 'GET', body } = {}) {
  try {
    const response = await fetch(`/api/ponto/payment-intents${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined
    });
    const data = await response.json().catch(() => null);
    return { ok: response.ok, status: response.status, data };
  } catch (err) {
    return { ok: false, status: 0, data: { msg: err.message } };
  }
}
